import React, {useEffect, useMemo, useState} from 'react';
import {getInvoicesInPayout} from "./payout-requests";
import {getErrorMessageForNonStandardAndStandardResponse} from "../../util/NetworkErrorUtil";
import {toast} from "react-toastify";
import {useParams} from "react-router";
import {Box, Grid} from "@mui/material";
import DownloadCsvIcon from "../../components/icons/DownloadCsvIcon";
import NoMatchingInvoicesFound from "../../components/NoMatchingInvoicesFound";
import {SORT_DIRECTION_ASC} from "../../util/ReportUtils";
import {getComparator, stableSort} from "../../util/Sorting";
import moment from "moment";
import {SubscriptionsCsvContainer, SubscriptionsSearchContainer} from '../../components/partner-subscriptions/styles';
import InvoicesInPayoutCard from "./InvoicesInPayoutCard";
import Busy from "../../components/Busy";
import SecurSpaceContainer from '../../components/common/SecurSpaceContainer';
import {formatInvoices, mapInvoicesForCsv, sortByOptions} from "./data";
import GroupSortFilter from "../../components/GroupSortFilter";
import CSVButton from "../../components/deprecatedUikit/CSVButton/CSVButton";
import Pagination from "../../components/deprecatedUikit/Pagination/Pagination";
import {SecurSpaceReportContainer, SecurSpaceReportTitle} from "../../components/common";

const InvoicesInPayoutReport = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [sort, setSort] = useState('');
  const [order, setOrder] = useState(SORT_DIRECTION_ASC);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const {payoutCorrelationId} = useParams();

  const handleSearchQueryChange = (event) => {
    setSearchFilter(event.searchFilter);
    handleSortChange(event.sortBy, event.sortDir);
  };

  const handleSortChange = (sort, order) => {
    setSort(sort);
    setOrder(order);
  };

  const handlePageChange = (_, selectedPage) => {
    setPage(selectedPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = useMemo(() =>
      stableSort(filteredInvoices, getComparator(order, sort)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [sort, page, rowsPerPage, filteredInvoices, order],
  );

  useEffect(() => {
    if (!payoutCorrelationId) {
      toast.error('No Payout Correlation Id Provided in URL');
    } else {
      Busy.set(true);
      getInvoicesInPayout(payoutCorrelationId)
        .then(({body}) => {
          if (body) {
            setInvoices(formatInvoices(body));
          }
        }).catch(error => {
        toast.error(getErrorMessageForNonStandardAndStandardResponse(error));
      }).finally(() => Busy.set(false));
    }
  }, [payoutCorrelationId]);


  useEffect(() => {
    let filtered = [];

    if (searchFilter) {
      let filterTokens = searchFilter.split(" ").map(value => value.toLocaleLowerCase());
      invoices.forEach((invoice) => {
        for (let token of filterTokens) {
          if (invoice?.transactionNumber?.toString().includes(token) ||
            invoice?.bookingNumber?.toString().includes(token) ||
            invoice?.buyerCompanyName?.toLocaleLowerCase().includes(token)
          ) {
            filtered.push(invoice)
            break;
          }
        }
      });

    } else {
      filtered = invoices;
    }

    setFilteredInvoices(filtered);
    setCsvData(mapInvoicesForCsv(filtered));
  }, [searchFilter, invoices]);

  const reportName = `Invoices In Payout_${moment().format("MM-DD-YYYY_hh:mm A")}`;

  return <SecurSpaceContainer>
    <SecurSpaceReportContainer flexDirection="column">
      <SecurSpaceReportTitle>Invoices In Payout</SecurSpaceReportTitle>
      <Grid container item justifyContent="space-between">
        <SubscriptionsSearchContainer item mt={4}>
          <GroupSortFilter
            filterCallback={handleSearchQueryChange}
            sortByOption={sortByOptions}
            sortDir={order}
            debounceTime={1000}
            searchPlaceholder={'Search by invoice number, booking number, or buyer'}
          />
        </SubscriptionsSearchContainer>
        <SubscriptionsCsvContainer item mt={4}>
          <CSVButton
            reportName={reportName}
            reportData={csvData}
            title="Download Report"
            startIcon={<DownloadCsvIcon/>}
          />
        </SubscriptionsCsvContainer>
      </Grid>
      <Box mt={4}>
        {
          visibleRows?.length ?
            <InvoicesInPayoutCard invoices={visibleRows} handleSortChange={handleSortChange}/>
            :
            <NoMatchingInvoicesFound
              item
              imageURI="/app-images/person-and-box.svg"
              message="No invoices found in this payout"
            />
        }
      </Box>
      <Pagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        count={invoices?.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </SecurSpaceReportContainer>
  </SecurSpaceContainer>
}

export default InvoicesInPayoutReport;
